"use client";

import { useRef } from "react";
import { CheckItem, CODE, GradText, SectionHead, SectionShell, Terminal } from "./kit";
import { gsap, NOT_REDUCED, playOnEnter, useGSAP } from "./kit/motion";

const CHECKS = [
  "One DNS record — that's the whole setup",
  "SSL issued and renewed automatically",
  "Nothing to install, nothing to maintain",
];

/**
 * One-record setup — the DNS terminal. The record "types" in on scroll
 * enter (clip-path print, the LlmShowcase pattern), then the verification
 * steps fade in one by one. Once-on-enter, no loops.
 */
export default function OneRecordSection() {
  const termRef = useRef<HTMLDivElement>(null);

  useGSAP(
    (_, contextSafe) => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const el = termRef.current;
        if (!el) return;
        let dispose: (() => void) | undefined;
        const arm = contextSafe!(() => {
          const tl = gsap
            .timeline({ paused: true })
            .fromTo(
              "[data-dns-record]",
              { clipPath: "inset(-2px 100% -2px 0)" },
              { clipPath: "inset(-2px 0% -2px 0)", duration: 0.6, ease: "steps(24)" },
            )
            .fromTo(
              "[data-dns-step]",
              { autoAlpha: 0, y: 6 },
              { autoAlpha: 1, y: 0, duration: 0.35, stagger: 0.45, ease: "power1.out" },
              "+=0.3",
            );
          // playOnEnter arms insure() itself at actual play time — never call insure() on a paused entrance at mount (it would force-complete it before the user scrolls to it).
          dispose = playOnEnter(tl, el);
        });
        arm();
        return () => dispose?.();
      });
    },
    { scope: termRef },
  );

  return (
    <SectionShell id="ref-setup">
      <div data-grid-block className="grid items-center gap-12 lg:grid-cols-[1fr_1.1fr]">
        <div className="min-w-0">
          <SectionHead
            eyebrow="One record"
            title={
              <>
                Live on your domain in <GradText>minutes.</GradText>
              </>
            }
            lead="Add the one DNS record your dashboard shows and you're done — we handle SSL, renewals and the routing engine behind it."
          />
          <div className="flex flex-col gap-3.5">
            {CHECKS.map((c) => (
              <CheckItem key={c}>{c}</CheckItem>
            ))}
          </div>
        </div>

        <div ref={termRef} className="min-w-0">
          <Terminal path="dns — yourbrand.com">
            <pre
              data-dns-record
              className="m-0 whitespace-pre-wrap font-[inherit] [overflow-wrap:anywhere]"
            >
              <span className={CODE.key}>A</span>  links.yourbrand.com{"  "}
              <span className={CODE.comment}>→</span>{"  "}
              <span className={CODE.string}>‹address from your dashboard›</span>
            </pre>
            <div className="mt-4 flex flex-col gap-1.5 font-jetbrains text-[13px]">
              <div data-dns-step className="text-data-green">
                ✓ record detected · checked every minute
              </div>
              <div data-dns-step className="text-data-green">
                ✓ SSL certificate issued
              </div>
              <div data-dns-step className="text-data-green">
                ✓ links.yourbrand.com is live
              </div>
            </div>
          </Terminal>
        </div>
      </div>
    </SectionShell>
  );
}
